import { Inject, Injectable } from '@angular/core';
import { Observable, Observer, Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { WebSocketConnectionState, WebSocketServiceBase, WebSocketServiceConfig, WSServiceFactory, WS_SERVICE_FACTORY } from 'types';
import { HWServiceConfig } from './hw-service-config';
import { EyePositionInfo, PhotoProcessingResult, PortraitServiceState, Position } from './hw-service-model';

const FMT_JPC_MIN_SIZE = 14100;
const FMT_JPC_MAX_SIZE = 14900;

export type ConvertFormatOptions = 'jpg' | 'bmp' | 'png' | 'jp2' | 'jpc';

export abstract class PortraitServiceBase {
  abstract connectionState$: Observable<PortraitServiceState>;

  abstract connect(): void;

  abstract upload(photo: Blob): Observable<void>;

  abstract download(copy: boolean): Observable<Blob>;

  abstract findEyes(): Observable<EyePositionInfo>;

  abstract setEyes(info: EyePositionInfo): Observable<void>;

  abstract convertJpg2(): Observable<void>;

  abstract convert<T extends ConvertFormatOptions>(
    format: T,
    options?: T extends 'jp2' ? { rate: number } : T extends 'jpc' ? { minSize: number; maxSize: number } : never,
  ): Observable<void>;

  abstract hdr(): Observable<void>;

  abstract binarizeSignature(): Observable<void>;

  abstract getID(): Observable<string>;

  abstract expasoft(): Observable<void>;

  abstract clear(): Observable<void>;

  abstract crop(): Observable<PhotoProcessingResult>;

  abstract disconnect(): void;
}

@Injectable()
export class PortraitService implements PortraitServiceBase {
  private processingState: PortraitServiceState = PortraitServiceState.Closed;
  private connectionStateSubject: Subject<PortraitServiceState>;
  private socket: WebSocketServiceBase;
  private photoToBeUploaded: Blob | null = null;
  private photoProcessingResult: PhotoProcessingResult | null = null;
  private responseType: ResponseType = ResponseType.None;
  private uploadingObserver: Observer<void> | null = null;
  private eyesPositionSearchObserver: Observer<EyePositionInfo> | null = null;
  private eyesPositionSetupObserver: Observer<void> | null = null;
  private convertationObserver: Observer<void> | null = null;
  private idObserver: Observer<string> | null = null;
  private downloadingObserver: Observer<Blob> | null = null;
  private photoProcessingObserver: Observer<PhotoProcessingResult> | null = null;
  private expasoftObserver: Observer<void> | null = null;
  private clearObserver: Observer<void> | null = null;

  public constructor(config: HWServiceConfig, @Inject(WS_SERVICE_FACTORY) createWebSocket: WSServiceFactory) {
    this.connectionStateSubject = new Subject<PortraitServiceState>();
    this.socket = createWebSocket(new WebSocketServiceConfig(config.getHWServiceUrl(), config.getPortraitServiceProtocol()));
    this.socket.messages$.subscribe(
      (message) => {
        this.onSocketMessageReceived(message);
      },
      (error) => {
        this.onSocketMessageReceived({ result: false, error: error });
      },
    );
    this.socket.connectionState$.subscribe(
      (state) => {
        this.onSocketConnectionStateChanged(state);
      },
      (error) => {
        this.connectionStateSubject.error(error);
      },
    );
  }

  get connectionState$(): Observable<PortraitServiceState> {
    return this.connectionStateSubject.pipe();
  }

  connect(): void {
    if (this.canConnect()) {
      this.socket.connect();
    } else {
      this.notifyOperationDenied('CONNECT');
    }
  }

  upload(photo: Blob): Observable<void> {
    return new Observable<void>((observer) => {
      if (this.canPerformOperation()) {
        this.photoToBeUploaded = photo;
        this.uploadingObserver = observer;
        this.responseType = ResponseType.UploadPermission;
        this.socket.send({ command: 'push-image' });
      } else {
        this.notifyOperationDenied('UPLOAD_PHOTO', observer);
      }
    });
  }

  download(copy: boolean): Observable<Blob> {
    return new Observable<Blob>((observer) => {
      if (this.canPerformOperation()) {
        this.downloadingObserver = observer;

        if (copy) {
          this.responseType = ResponseType.Copy;
          this.socket.send({ command: 'copy' });
        } else {
          this.responseType = ResponseType.Download;
          this.socket.send({ command: 'pop-image' });
        }
      } else {
        this.notifyOperationDenied('DOWNLOAD_PHOTO', observer);
      }
    });
  }

  findEyes(): Observable<EyePositionInfo> {
    return new Observable<EyePositionInfo>((observer) => {
      if (this.canPerformOperation()) {
        this.eyesPositionSearchObserver = observer;
        this.socket.send({ command: 'find-eyes' });
        this.responseType = ResponseType.EyesPositionSearch;
      } else {
        this.notifyOperationDenied('FIND_EYES', observer);
      }
    });
  }

  setEyes(info: EyePositionInfo): Observable<void> {
    return new Observable<void>((observer) => {
      if (this.canPerformOperation()) {
        this.eyesPositionSetupObserver = observer;
        this.socket.send({
          command: 'set-eyes',
          left_x: info.left.x,
          left_y: info.left.y,
          right_x: info.right.x,
          right_y: info.right.y,
        });
        this.responseType = ResponseType.EyesPositionSetup;
      } else {
        this.notifyOperationDenied('SET_EYES', observer);
      }
    });
  }

  convertJpg2(): Observable<void> {
    return new Observable<void>((observer) => {
      if (this.canPerformOperation()) {
        this.convertationObserver = observer;
        this.socket.send({
          command: 'convert',
          format: 'png',
        });
        this.responseType = ResponseType.Convertation;
      } else {
        this.notifyOperationDenied('CONVERT_PHOTO', observer);
      }
    });
  }

  convert<T extends ConvertFormatOptions>(
    format: T,
    options?: T extends 'jp2' ? { rate: number } : T extends 'jpc' ? { minSize: number; maxSize: number } : never,
  ): Observable<void> {
    return new Observable<void>((observer) => {
      if (this.canPerformOperation()) {
        this.convertationObserver = observer;
        this.socket.send({
          command: 'convert',
          format: format,
          ...options,
        });
        this.responseType = ResponseType.Convertation;
      } else {
        this.notifyOperationDenied('CONVERT_PHOTO', observer);
      }
    });
  }

  hdr(): Observable<void> {
    return new Observable<void>((observer) => {
      if (this.canPerformOperation()) {
        this.convertationObserver = observer;
        this.socket.send({
          command: 'HDR',
        });
        this.responseType = ResponseType.Convertation;
      } else {
        this.notifyOperationDenied('CONVERT_PHOTO', observer);
      }
    });
  }

  binarizeSignature(): Observable<void> {
    return new Observable<void>((observer) => {
      if (this.canPerformOperation()) {
        this.convertationObserver = observer;
        this.socket.send({
          command: 'binarize-signature',
        });
        this.responseType = ResponseType.Convertation;
      } else {
        this.notifyOperationDenied('CONVERT_PHOTO', observer);
      }
    });
  }

  getID(): Observable<string> {
    return new Observable<string>((observer) => {
      if (this.canPerformOperation()) {
        this.idObserver = observer;
        this.socket.send({
          command: 'get-id',
        });
        this.responseType = ResponseType.ID;
      } else {
        this.notifyOperationDenied('GET_ID', observer);
      }
    });
  }

  expasoft(): Observable<void> {
    return new Observable<void>((observer) => {
      if (this.canPerformOperation()) {
        this.expasoftObserver = observer;
        this.socket.send({ command: 'crop', kind: 'expasoft' });
        this.responseType = ResponseType.Expasoft;
      } else {
        this.notifyOperationDenied('expasoft', observer);
      }
    });
  }

  clear(): Observable<void> {
    return new Observable<void>((observer) => {
      if (this.canPerformOperation()) {
        this.clearObserver = observer;
        this.responseType = ResponseType.Clear;
        this.socket.send({ command: 'clear' });
      } else {
        this.notifyOperationDenied('Clear', observer);
      }
    });
  }

  crop(): Observable<PhotoProcessingResult> {
    return new Observable<PhotoProcessingResult>((observer) => {
      if (this.canPerformOperation()) {
        this.photoProcessingObserver = observer;
        this.socket.send({ command: 'crop', kind: 'chip' });
        this.photoProcessingResult = new PhotoProcessingResult();
        this.responseType = ResponseType.ChipPhoto;
      } else {
        this.notifyOperationDenied('CROP', observer);
      }
    });
  }

  disconnect(): void {
    if (this.canDisconnect()) {
      this.resetResponseSettings();
      this.socket.disconnect();
    } else {
      this.notifyOperationDenied('DISCONNECT');
    }
  }

  private canConnect(): boolean {
    return this.processingState === PortraitServiceState.Closed;
  }

  private canDisconnect(): boolean {
    return this.processingState !== PortraitServiceState.Closed;
  }

  private canPerformOperation(): boolean {
    return this.processingState !== PortraitServiceState.Closed && this.responseType === ResponseType.None;
  }

  private setProcessingState(state: PortraitServiceState): void {
    if (this.processingState !== state) {
      this.processingState = state;
      this.connectionStateSubject.next(state);
    }
  }

  private onSocketConnectionStateChanged(state: WebSocketConnectionState): void {
    switch (state) {
      case WebSocketConnectionState.Opened:
        this.setProcessingState(PortraitServiceState.Ready);
        break;
      case WebSocketConnectionState.Connecting:
      case WebSocketConnectionState.Disconnecting:
      case WebSocketConnectionState.Closed:
        this.setProcessingState(PortraitServiceState.Closed);
        break;
    }
  }
  /* САМЫЙ ГЛАВНЫЙ СВИТЧ */
  private onSocketMessageReceived(message: any): void {
    switch (this.responseType) {
      case ResponseType.UploadPermission:
        this.onUploadPermissionResponseReceived(message);
        break;
      case ResponseType.UploadComplete:
        this.onUploadCompleteResponseReceived(message);
        break;
      case ResponseType.Convertation:
        this.onConversationResponseReceived(message);
        break;
      case ResponseType.ID:
        this.onIdResponseReceived(message);
        break;
      case ResponseType.Copy:
        this.onCopyResponseReceived(message);
        break;
      case ResponseType.Download:
        this.onDownloadResponseReceived(message);
        break;
      case ResponseType.EyesPositionSearch:
        this.onEyesPositionSearchResponseReceived(message);
        break;
      case ResponseType.EyesPositionSetup:
        this.onEyesPositionSetupResponseReceived(message);
        break;
      case ResponseType.ChipPhoto:
        this.onChipPhotoResponseReceived(message);
        break;
      case ResponseType.PortraitPhoto:
        this.onPortraitPhotoResponseReceived(message);
        break;
      case ResponseType.Expasoft:
        this.onExpasoftResponseReceived(message);
        break;
      case ResponseType.Clear:
        this.onClearResponseReceived(message);
        break;
    }
  }

  private onExpasoftResponseReceived(message: any) {
    if (!this.expasoftObserver) {
      return;
    }
    const obs = this.expasoftObserver;
    this.expasoftObserver = null;
    this.responseType = ResponseType.None;
    if (message.result) {
      obs.next(undefined);
      obs.complete();
    } else {
      obs.error(message.error);
    }
  }

  private onClearResponseReceived(message: any) {
    if (!this.clearObserver) {
      return;
    }
    const obs = this.clearObserver;
    this.clearObserver = null;
    this.responseType = ResponseType.None;
    if (message.result) {
      obs.next(undefined);
      obs.complete();
    } else {
      obs.error(message.error);
    }
  }

  private onUploadPermissionResponseReceived(message: any): void {
    if (!this.uploadingObserver) {
      return;
    }
    if (message.result) {
      this.responseType = ResponseType.UploadComplete;
      this.socket.send(this.photoToBeUploaded);
    } else {
      const obs = this.uploadingObserver;

      this.responseType = ResponseType.None;
      this.uploadingObserver = null;
      this.photoToBeUploaded = null;

      obs.error(message.error);
    }
  }

  private onConversationResponseReceived(message: any): void {
    if (!this.convertationObserver) {
      return;
    }

    const obs = this.convertationObserver;

    this.responseType = ResponseType.None;
    this.convertationObserver = null;

    if (message.result) {
      obs.next(message);
      obs.complete();
    } else {
      obs.error(message.error);
    }
  }

  private onIdResponseReceived(message: any): void {
    if (!this.idObserver) {
      return;
    }
    if (message.result) {
      this.responseType = ResponseType.None;
      this.idObserver.next(message.id);
      this.idObserver.complete();
    } else {
      this.responseType = ResponseType.None;
      this.connectionStateSubject.error(message.error);
      this.idObserver.error(message.error);
      this.idObserver = null;
    }
  }

  private onUploadCompleteResponseReceived(message: any): void {
    if (!this.uploadingObserver) {
      return;
    }

    const obs = this.uploadingObserver;

    this.responseType = ResponseType.None;
    this.uploadingObserver = null;
    this.photoToBeUploaded = null;

    if (message.result) {
      obs.next(message);
      obs.complete();
    } else {
      obs.error(message.error);
    }
  }

  private onCopyResponseReceived(message: any): void {
    if (!this.downloadingObserver) {
      return;
    }
    if (message.result) {
      this.responseType = ResponseType.Download;
      this.socket.send({ command: 'pop-image' });
    } else {
      this.downloadingObserver.error(message.error);
      this.downloadingObserver = null;
      this.responseType = ResponseType.None;
    }
  }

  private onDownloadResponseReceived(message: any): void {
    if (!this.downloadingObserver) {
      return;
    }

    const obs = this.downloadingObserver;

    this.responseType = ResponseType.None;
    this.downloadingObserver = null;

    if (message instanceof Blob) {
      obs.next(message);
      obs.complete();
    } else {
      obs.error(message.error);
    }
  }

  private onEyesPositionSearchResponseReceived(message: any): void {
    if (!this.eyesPositionSearchObserver) {
      return;
    }
    if (message.result) {
      var info = new EyePositionInfo();
      info.left = new Position(message.left_x, message.left_y);
      info.right = new Position(message.right_x, message.right_y);
      this.eyesPositionSearchObserver.next(info);
      this.eyesPositionSearchObserver.complete();
    } else {
      this.eyesPositionSearchObserver.error(message.error);
    }
    this.eyesPositionSearchObserver = null;
    this.responseType = ResponseType.None;
  }

  private onEyesPositionSetupResponseReceived(message: any): void {
    if (!this.eyesPositionSetupObserver) {
      return;
    }
    const obs = this.eyesPositionSetupObserver;
    this.eyesPositionSetupObserver = null;
    this.responseType = ResponseType.None;
    if (message.result) {
      obs.next(undefined);
      obs.complete();
    } else {
      obs.error(message.error);
    }
  }

  private onChipPhotoResponseReceived(message: any): void {
    if (!this.photoProcessingObserver || !this.photoProcessingResult) {
      return;
    }
    if (message.result) {
      this.responseType = ResponseType.None;
      this.convert('jpc', { minSize: FMT_JPC_MIN_SIZE, maxSize: FMT_JPC_MAX_SIZE })
        .pipe(
          switchMap(() => this.convertJpg2()),
          switchMap(() => this.download(false)),
        )
        .subscribe(
          (blob) => {
            if (!this.photoProcessingObserver || !this.photoProcessingResult) {
              return;
            }
            this.photoProcessingResult.chipPhoto = blob;
            setTimeout(() => {
              this.responseType = ResponseType.PortraitPhoto;
              this.socket.send({ command: 'crop', kind: 'print' });
            }, 1);
          },
          (error) => {
            if (!this.photoProcessingObserver || !this.photoProcessingResult) {
              return;
            }
            this.photoProcessingObserver.error(error);
            this.responseType = ResponseType.None;
            this.photoProcessingObserver = null;
            this.photoProcessingResult = null;
          },
        );
    } else {
      this.photoProcessingObserver.error(message.error);
      this.responseType = ResponseType.None;
      this.photoProcessingObserver = null;
      this.photoProcessingResult = null;
    }
  }

  private onPortraitPhotoResponseReceived(message: any): void {
    if (!this.photoProcessingObserver || !this.photoProcessingResult) {
      return;
    }
    if (message.result) {
      this.responseType = ResponseType.None;
      const quality = message.quality;
      this.download(false).subscribe(
        (blob) => {
          if (!this.photoProcessingObserver || !this.photoProcessingResult) {
            return;
          }
          this.photoProcessingResult.portraitPhoto = blob;
          this.photoProcessingResult.quality = quality;
          this.photoProcessingObserver.next(this.photoProcessingResult);
          this.photoProcessingObserver.complete();
          this.responseType = ResponseType.None;
          this.photoProcessingObserver = null;
          this.photoProcessingResult = null;
        },
        (error) => {
          if (!this.photoProcessingObserver || !this.photoProcessingResult) {
            return;
          }
          this.photoProcessingObserver.error(error);
          this.responseType = ResponseType.None;
          this.photoProcessingObserver = null;
          this.photoProcessingResult = null;
        },
      );
    } else {
      this.photoProcessingObserver.error(message.error);
      this.responseType = ResponseType.None;
      this.photoProcessingObserver = null;
      this.photoProcessingResult = null;
    }
  }

  private notifyOperationDenied<T>(operation: string, observer: Observer<T> | null = null): void {
    var message =
      'Cannot perform operation "' +
      operation +
      '". ' +
      'Processing state: ' +
      PortraitServiceState[this.processingState] +
      '. ' +
      'Expected response: ' +
      ResponseType[this.responseType] +
      '.';

    if (observer) {
      observer.error(message);
    } else {
      this.connectionStateSubject.error(message);
    }
  }

  private resetResponseSettings(): void {
    this.responseType = ResponseType.None;
    this.uploadingObserver = null;
    this.downloadingObserver = null;
    this.eyesPositionSearchObserver = null;
    this.photoProcessingObserver = null;
    this.photoToBeUploaded = null;
    this.photoProcessingResult = null;
  }
}

enum ResponseType {
  None,
  Copy,
  UploadPermission,
  UploadComplete,
  Download,
  EyesPositionSearch,
  EyesPositionSetup,
  Convertation,
  ID,
  Expasoft,
  Clear,
  ChipPhoto,
  PortraitPhoto,
}
